/**
 * AI-tolkning av en inklistrad mailsignatur när en kontakt läggs till på en
 * befintlig kund (kundkortets "Ny kontakt"). Samma tolkning som i
 * "Ny kund" (signatureAI.js).
 *
 *   const sig = useSignaturTolkning(kund);
 *   const kontaktfalt = await sig.tolka(text);   // fyll tomma kontaktfält
 *   <SignaturStatus sig={sig} kund={kund} />     // status + kundens uppgifter
 *   sig.kundUppdateringar()                      // sparas med kontakten
 *
 * Kundens uppgifter i signaturen (telefon, adresser, webbplats, org.nr):
 * tomma fält fylls, avvikande värden väljer användaren. Kundens namn ändras
 * aldrig; hör signaturen till ett annat företag visas en varning och
 * kundens uppgifter lämnas orörda.
 */
import React, { useCallback, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { parseSignature, toFormValues, mergePlan, companyNameKey, COMPANY_FIELD_LABELS } from './signatureAI';
import MergeChoices, { pickUpdates } from './MergeChoices';

/** Hör signaturens företagsnamn till en annan kund än den man står på? */
export function annatForetag(kundNamn, signaturNamn) {
  const a = companyNameKey(kundNamn);
  const b = companyNameKey(signaturNamn);
  if (!a || !b) return false;
  return !(a.includes(b) || b.includes(a));
}

export function useSignaturTolkning(kund) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [flera, setFlera] = useState(false);
  const [plan, setPlan] = useState([]);
  const [val, setVal] = useState({});
  const [signaturForetag, setSignaturForetag] = useState('');
  const [annat, setAnnat] = useState(false);

  /** Tolkar texten. Returnerar kontaktens fält ({} vid fel). */
  const tolka = useCallback(async text => {
    setStatus('loading');
    try {
      const resultat = await parseSignature(text);
      const { company, contact } = toFormValues(resultat);
      const fel = annatForetag(kund?.name, company.name);
      setAnnat(fel);
      setPlan(fel ? [] : mergePlan(kund, company, COMPANY_FIELD_LABELS));
      setVal({});
      setFlera(resultat.multipleSignatures);
      setSignaturForetag(company.name || '');
      setStatus('done');
      return contact;
    } catch (e) {
      console.error('Signaturtolkning misslyckades:', e);
      setStatus('error');
      return {};
    }
  }, [kund]);

  const aterstall = useCallback(() => {
    setStatus('idle'); setPlan([]); setVal({}); setFlera(false); setSignaturForetag(''); setAnnat(false);
  }, []);

  return {
    status, flera, plan, val, setVal, signaturForetag, annat, tolka, aterstall,
    /** Kundens fält som ska sparas: tomma fält + valda avvikande värden. */
    kundUppdateringar: () => (status === 'done' ? pickUpdates(plan, val, 'company.') : {}),
  };
}

/** Status för tolkningen och kundens uppgifter ur signaturen. */
export function SignaturStatus({ sig, kund }) {
  if (sig.status === 'idle') return null;
  if (sig.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Tolkar signaturen med AI…</Typography>
      </Box>
    );
  }
  if (sig.status === 'error') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }} onClose={sig.aterstall}>
        <Typography variant="caption">
          Det gick inte att tolka texten automatiskt. Fyll i uppgifterna manuellt – texten finns kvar i rutan.
        </Typography>
      </Alert>
    );
  }
  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {sig.flera && (
        <Alert severity="info" sx={{ py: 0 }}>
          <Typography variant="caption">Texten innehöll flera signaturer — uppgifterna kommer från den översta.</Typography>
        </Alert>
      )}
      {sig.annat && (
        <Alert severity="warning" sx={{ py: 0 }}>
          <Typography variant="caption">
            Signaturen verkar höra till <b>{sig.signaturForetag}</b>, inte {kund.name}. Kontrollera att kontakten
            ska ligga här. Kundens uppgifter uppdateras inte från den här signaturen.
          </Typography>
        </Alert>
      )}
      {!sig.annat && (
      <Box sx={{ borderRadius: 2, p: 1.5, border: '1px solid #b39ddb', bgcolor: '#f7f3ff' }}>
        <Typography variant="caption" fontWeight="bold" color="#5E35B1"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
          Uppgifter om {kund?.name || 'kunden'} i signaturen – sparas med kontakten
        </Typography>
        <MergeChoices rows={sig.plan} choices={sig.val} prefix="company."
          onChoose={(k, v) => sig.setVal(c => ({ ...c, [k]: v }))} />
      </Box>
      )}
    </Box>
  );
}
